import { FunctionComponent, MouseEvent, useEffect, useState, ReactNode } from 'react'
import { css, cx } from '@emotion/css'
import { Theme, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import { NumericFormat } from 'react-number-format'
import { InputValues } from '@/components/TipSelect/InputCurrency'
import { calcTipAmount } from '@/libs/helpers/utils'
import { Button, Grid, Modal, Text } from '@/tbui'

type CustomTipType = 'dollar' | 'percentage'
export interface TipTileProps {
  selected: boolean
}

const inputStyle = (theme: Theme): string => css`
  border: none;
  border-bottom: 2px solid ${theme.palette.TEXT_SECONDARY};
  padding: '8px 0';
  background: transparent;
  transition: 0.2s ease;
  border-radius: 0;
  font-size: 14px;
  outline: none;
  text-align: center;
  padding: 0 0 4px 0;
  max-width: 10ch;
  margin: 0;
  opacity: 1;
  color: ${theme.palette.TEXT_SECONDARY};

  &:focus {
    border-bottom: 2px solid ${theme.palette.PRIMARY};
  }

  &.is-disabled {
    opacity: 0;
  }
`

const CustomTipInputWrapper = styled(Grid)<{ disabled: boolean }>`
  border-radius: ${(props): string => props.theme.shape.RADIUS};
  align-items: center;
  margin: 0;
  background-color: ${(props): string =>
    props.disabled ? props.theme.palette.GRAY_1 : props.theme.palette.SECONDARY};
`

interface CustomTipModalProps {
  onChange?: (tipAmount: string) => void
  onClose: () => void
  selectedTip: string
  open: boolean
  totalAfterTax: string
  isCartLoading: boolean
}

const CustomTipModal: FunctionComponent<CustomTipModalProps> = ({
  selectedTip,
  onChange,
  onClose,
  open,
  totalAfterTax,
  isCartLoading,
}) => {
  // STATES
  const [customTipType, setCustomTipType] = useState<CustomTipType>('dollar')
  const [customTipPercentage, setCustomTipPercentage] = useState<string>('')
  const [customTipAmount, setCustomTipAmount] = useState<string>('')

  // HOOKS
  const theme = useTheme()
  const { t } = useTranslation()

  // CONSTS
  const customTipTypes: CustomTipType[] = ['dollar', 'percentage']
  const isPercentageTip = customTipType === 'percentage'

  useEffect(() => {
    // recalculate only if tip is percentage
    if (selectedTip !== 'custom' || isPercentageTip) {
      const tipAmt = calcTipAmount(totalAfterTax, customTipPercentage)
      setCustomTipAmount(tipAmt)
      if (onChange != null) {
        onChange(tipAmt)
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customTipPercentage, totalAfterTax])

  useEffect(() => {
    if (selectedTip !== 'custom') {
      setCustomTipPercentage(selectedTip)
    }
  }, [selectedTip])

  // HANDLERS
  const handleCustomTipSubmit = (e?: MouseEvent): void => {
    // event also fires on keydown :| TODO => investigate
    if (e != null) {
      // prevent checkout form from submitting on custom tip save
      e.preventDefault()
    }
    let customTipAmt = customTipAmount
    // re-calc tip amount on submit
    if (isPercentageTip) {
      customTipAmt = calcTipAmount(totalAfterTax, customTipPercentage)
      setCustomTipAmount(customTipAmt)
    }
    if (onChange != null) {
      onChange(customTipAmt)
    }
    onClose()
  }

  // RENDERERS
  const renderCustomTipTypes = (): ReactNode => {
    const renderInput = (tipType: CustomTipType, isDisabled: boolean): ReactNode => {
      if (tipType === 'dollar') {
        // is amount
        const handleCustomDollarTipChange = (inputValues: InputValues): void => {
          const { value: customTip } = inputValues
          setCustomTipAmount(customTip === '' ? '0.00' : customTip)
        }

        return (
          <NumericFormat
            placeholder="$0.00"
            isAllowed={(inputValues: InputValues): boolean => {
              if ((inputValues.floatValue ?? 0) > 1000) {
                return false
              }
              return true
            }}
            onValueChange={handleCustomDollarTipChange}
            allowNegative={false}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator
            data-test={`custom-${tipType}-tip`}
            prefix="$"
            name={`custom-${tipType}-tip`}
            value={customTipAmount}
            className={cx(inputStyle(theme), `${isDisabled ? 'is-disabled' : ''}`)}
          />
        )
      }

      // is percentage
      const handleCustomPercentTipChange = (inputValues: InputValues): void => {
        const { value: customTip } = inputValues
        setCustomTipPercentage(customTip)
      }

      return (
        <NumericFormat
          placeholder="0%"
          isAllowed={(inputValues: InputValues): boolean => {
            if (inputValues.value.length > 2) {
              return false
            }
            return true
          }}
          onValueChange={handleCustomPercentTipChange}
          allowNegative={false}
          decimalScale={0}
          data-test={`custom-${tipType}-tip`}
          suffix=" %"
          name={`custom-${tipType}-tip`}
          value={customTipPercentage}
          className={cx(inputStyle(theme), `${isDisabled ? 'is-disabled' : ''}`)}
        />
      )
    }

    return (
      <>
        {customTipTypes.map((tipType: CustomTipType) => {
          const isDisabled = customTipType !== tipType

          return (
            <Grid key={tipType}>
              <CustomTipInputWrapper
                onClick={(): void => setCustomTipType(tipType)}
                role={isDisabled ? 'button' : undefined}
                data-test={`custom-${tipType}-tip`}
                justify="space-between"
                disabled={isDisabled}
                template="auto auto"
                p="1rem"
              >
                <Text color={isDisabled ? 'TEXT' : 'TEXT_SECONDARY'} bold={!isDisabled}>
                  {t('checkout.payment.custom_tip', { context: tipType })}
                </Text>
                {renderInput(tipType, isDisabled)}
              </CustomTipInputWrapper>
            </Grid>
          )
        })}
      </>
    )
  }

  return (
    <Modal
      onClose={() => {
        handleCustomTipSubmit()
      }}
      variant="SMALL"
      open={open}
    >
      <Grid gap="20px">
        <Text type="H3">{t('checkout.payment.tip_custom_title')}</Text>
        {renderCustomTipTypes()}
        <Button
          onClick={handleCustomTipSubmit}
          data-test="save-tip-button"
          isLoading={isCartLoading}
        >
          {t('checkout.payment.tip_custom_submit')}
        </Button>
      </Grid>
    </Modal>
  )
}

export default CustomTipModal
